/**
 * Portal Comprovantes Page
 *
 * Two tabs:
 * - "Meus Comprovantes": history list with status badges and filters
 * - "Enviar Comprovante": authenticated upload form (no CNPJ needed)
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PendenciaCard, type PendenciaPortal } from "@/components/portal/PendenciaCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertCircle,
  Camera,
  CheckCircle,
  Clock,
  FileImage,
  Loader2,
  RefreshCw,
  Upload,
} from "lucide-react";
import portalApi from "@/services/portalApi";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import CameraGuide from "@/components/lotese/CameraGuide";
import {
  ACCEPT_COMPROVANTE,
  ImagemInvalidaError,
  compressImage,
  prepararComprovanteParaEnvio,
} from "@/components/lotese/imageUtils";
import OCRProgressiveReceipt from "@/components/comprovante-ocr/OCRProgressiveReceipt";
import UploadFallbackMenu from "@/components/lotese/UploadFallbackMenu";
import { OCRValidationResult } from "@/components/comprovante-ocr/OCRValidationResult";
import { PortalComprovanteDetailDialog } from "@/components/portal/PortalComprovanteDetailDialog";
import { useOCRValidation } from "@/hooks/useOCRValidation";
import { mapOCRResponseToProgressive } from "@/lib/ocr-adapter";

const hasCameraSupport = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

// ============================================================================
// Types
// ============================================================================

interface PortalComprovante {
  id: string;
  valor: number;
  status: string;
  status_amigavel?: string;
  categoria?: "info" | "success" | "warning" | "danger";
  created_at: string;
  ges_ponto_codigo: string;
  ges_ponto_nome: string;
  ocr_processado_em: string | null;
  // Dados que o OCR leu do recibo do próprio parceiro (Fatia D/E)
  comprovante_data_hora?: string | null;
  ocr_valor?: number | null;
  /** `null` quando nunca foi pendenciado. */
  pendencia?: PendenciaPortal | null;
  substitui_comprovante_id?: string | null;
}

interface TerminalOption {
  ges_ponto_id: string;
  codigo: string;
  nome: string;
}

// ============================================================================
// Status helpers
// ============================================================================

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    icon: React.ElementType;
  }
> = {
  recebido: { label: "Recebido", variant: "default", icon: Clock },
  processando_ocr: { label: "Processando", variant: "secondary", icon: Loader2 },
  aguardando_conciliacao: { label: "Aguardando", variant: "outline", icon: Clock },
  conciliado: { label: "Conciliado", variant: "default", icon: CheckCircle },
  divergencia: { label: "Divergencia", variant: "destructive", icon: AlertCircle },
  rejeitado: { label: "Rejeitado", variant: "destructive", icon: AlertCircle },
  erro_ocr: { label: "Erro OCR", variant: "destructive", icon: AlertCircle },
  em_baixa: { label: "Em Baixa", variant: "secondary", icon: Clock },
  baixa_concluida: { label: "Baixa OK", variant: "default", icon: CheckCircle },
  // Fallback local; o backend manda "Reenvie o comprovante" em status_amigavel.
  pendenciado: { label: "Reenvie o comprovante", variant: "outline", icon: AlertCircle },
};

const CATEGORIA_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  success: "default",
  info: "secondary",
  warning: "outline",
  danger: "destructive",
};

// Prefere o status amigável do backend (privacy-safe, fonte única). Faz fallback
// pro STATUS_CONFIG local só se o backend ainda não mandou (compat de deploy).
function StatusBadge({ c }: { c: PortalComprovante }) {
  if (c.status_amigavel) {
    const variant = CATEGORIA_VARIANT[c.categoria || "info"] || "secondary";
    return <Badge variant={variant}>{c.status_amigavel}</Badge>;
  }
  const config = STATUS_CONFIG[c.status] || {
    label: c.status,
    variant: "outline" as const,
    icon: Clock,
  };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}

// ============================================================================
// API hooks
// ============================================================================

function usePortalComprovantes(page: number, statusFilter: string | null) {
  return useQuery({
    queryKey: ["portal", "comprovantes", page, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), page_size: "20" });
      if (statusFilter) params.set("status_filter", statusFilter);
      const { data } = await portalApi.get(`/portal/comprovantes?${params}`);
      return data as { items: PortalComprovante[]; total: number; page: number; pages: number };
    },
  });
}

function usePortalTerminais() {
  return useQuery({
    queryKey: ["portal", "comprovantes", "terminais"],
    queryFn: async () => {
      const { data } = await portalApi.get("/portal/comprovantes/terminais");
      return data.terminais as TerminalOption[];
    },
  });
}

function useUploadComprovante() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      ges_ponto_id: string;
      valor: string;
      imagem: Blob;
      /** Preenchido quando o envio veio do card de pendência — liga o novo
       * comprovante ao pendenciado e fecha a pendência (RN-6/D9). */
      substitui_comprovante_id?: string;
    }) => {
      const formData = new FormData();
      formData.append("ges_ponto_id", payload.ges_ponto_id);
      formData.append("valor", payload.valor);
      formData.append("imagem", payload.imagem, "comprovante.jpg");
      if (payload.substitui_comprovante_id) {
        formData.append("substitui_comprovante_id", payload.substitui_comprovante_id);
      }
      const { data } = await portalApi.post("/portal/comprovantes", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portal", "comprovantes"] });
    },
  });
}

// ============================================================================
// History Tab
// ============================================================================

function ComprovantesHistoryTab() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = usePortalComprovantes(page, statusFilter);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end">
        <div className="w-48">
          <Label>Status</Label>
          <Select
            value={statusFilter || "__all__"}
            onValueChange={(v) => {
              setStatusFilter(v === "__all__" ? null : v);
              setPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>
                  {cfg.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
        </Button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {(error as Error).message || "Erro ao carregar comprovantes"}
          </AlertDescription>
        </Alert>
      )}

      {/* List */}
      {data && data.items.length > 0 && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {data.total} comprovante{data.total !== 1 ? "s" : ""} encontrado
            {data.total !== 1 ? "s" : ""}
          </p>
          {data.items.map((c) => (
            <Card
              key={c.id}
              className="hover:bg-muted/50 transition-colors cursor-pointer"
              onClick={() => setSelectedId(c.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setSelectedId(c.id);
              }}
            >
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium">{c.ges_ponto_codigo}</span>
                    <span className="text-sm text-muted-foreground">— {c.ges_ponto_nome}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enviado em {formatDate(c.created_at)}
                    {c.comprovante_data_hora ? ` · depósito ${c.comprovante_data_hora}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold">{formatCurrency(c.valor)}</span>
                  <StatusBadge c={c} />
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Pagination */}
          {data.pages > 1 && (
            <div className="flex justify-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </Button>
              <span className="text-sm py-2">
                Pagina {page} de {data.pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= data.pages}
                onClick={() => setPage(page + 1)}
              >
                Proxima
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Empty */}
      {data && data.items.length === 0 && (
        <div className="text-center py-12">
          <FileImage className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nenhum comprovante encontrado</h3>
          <p className="text-muted-foreground">
            {statusFilter
              ? "Tente alterar o filtro de status"
              : 'Envie seu primeiro comprovante na aba "Enviar"'}
          </p>
        </div>
      )}

      <PortalComprovanteDetailDialog
        comprovanteId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

// ============================================================================
// Upload Tab
// ============================================================================

function formatMoneyMask(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  const num = parseInt(digits, 10);
  return (num / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function ComprovantesUploadTab({
  reenvio,
  onReenvioConcluido,
}: {
  /** Preenchido quando o parceiro veio do card de pendência. */
  reenvio?: { id: string; ponto: string } | null;
  onReenvioConcluido?: () => void;
}) {
  const { data: terminais, isLoading: terminaisLoading } = usePortalTerminais();
  const uploadMutation = useUploadComprovante();
  const {
    status: ocrStatus,
    result: ocrResult,
    validar: validarOCR,
    reset: resetOCR,
  } = useOCRValidation();

  const [selectedTerminal, setSelectedTerminal] = useState("");
  const [valorDisplay, setValorDisplay] = useState("");
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  /** Mensagem de RN-3 já redigida para o parceiro; null quando não há erro. */
  const [erroImagem, setErroImagem] = useState<string | null>(null);
  const [animationComplete, setAnimationComplete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Adapter: OCRValidationResponse uses `detectado` but OCRResultLike expects `encontrado`.
  const adaptedOcrResult = useMemo(() => mapOCRResponseToProgressive(ocrResult), [ocrResult]);

  const valor = parseFloat(valorDisplay.replace(/\./g, "").replace(",", ".")) || 0;
  const terminalCodigo = terminais?.find((t) => t.ges_ponto_id === selectedTerminal)?.codigo;

  const runOCR = useCallback(
    async (blob: Blob) => {
      await validarOCR({
        imagem: blob,
        valor_esperado: valor > 0 ? valor : undefined,
        terminal_esperado: terminalCodigo,
      });
    },
    [validarOCR, valor, terminalCodigo]
  );

  const handleCameraCapture = useCallback(
    async (blob: Blob) => {
      setAnimationComplete(false);
      setShowCamera(false);
      setIsCompressing(true);
      try {
        const compressed = await compressImage(blob);
        setImageBlob(compressed);
        setIsCompressing(false);
        await runOCR(compressed);
      } catch {
        setIsCompressing(false);
      }
    },
    [runOCR]
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";

      setAnimationComplete(false);
      setErroImagem(null);
      setIsCompressing(true);
      try {
        // RN-3: valida formato e converte para JPEG. Levanta
        // ImagemInvalidaError com mensagem já redigida para o parceiro.
        const compressed = await prepararComprovanteParaEnvio(file);
        setImageBlob(compressed);
        setIsCompressing(false);
        await runOCR(compressed);
      } catch (e) {
        setIsCompressing(false);
        // O `catch` aqui era MUDO: o parceiro escolhia a foto, o spinner girava,
        // parava, e nada acontecia — sem imagem e sem explicação. Ele repetia o
        // mesmo passo indefinidamente.
        setErroImagem(
          e instanceof ImagemInvalidaError
            ? e.message
            : "Não foi possível processar a imagem. Tire outra foto e tente novamente."
        );
      }
    },
    [runOCR]
  );

  const handleRetake = useCallback(() => {
    // Limpa tudo ANTES de abrir a câmera — garante que o próximo ciclo
    // de captura/OCR começa do zero, sem resíduo do resultado anterior.
    setImageBlob(null);
    resetOCR();
    setAnimationComplete(false);
    // Reabre a câmera automaticamente — sem isso, o usuário clica "Tirar
    // outra foto" e fica olhando pra tela vazia (bug clássico de UX).
    setShowCamera(true);
  }, [resetOCR]);

  const handleSubmit = useCallback(() => {
    if (!imageBlob || !selectedTerminal || valor <= 0) return;
    uploadMutation.mutate(
      {
        ges_ponto_id: selectedTerminal,
        valor: String(valor),
        imagem: imageBlob,
        // Só vai quando o envio nasceu do card de pendência — é o que fecha a
        // pendência e liga os dois registros (RN-6/D9).
        substitui_comprovante_id: reenvio?.id,
      },
      { onSuccess: () => onReenvioConcluido?.() }
    );
  }, [imageBlob, selectedTerminal, valor, uploadMutation, reenvio, onReenvioConcluido]);

  const resetForm = () => {
    setSelectedTerminal("");
    setValorDisplay("");
    setImageBlob(null);
    resetOCR();
    setAnimationComplete(false);
    uploadMutation.reset();
  };

  if (uploadMutation.isSuccess) {
    const result = uploadMutation.data;
    return (
      <div className="text-center py-12 space-y-4">
        <div className="flex justify-center">
          <div className="rounded-full bg-emerald-100 p-4 animate-in zoom-in-50 duration-400">
            <CheckCircle className="h-14 w-14 text-emerald-600" />
          </div>
        </div>
        <h3 className="text-xl font-bold">Comprovante enviado!</h3>
        {result?.terminal_codigo && (
          <p className="text-slate-600">
            Ponto Lotese: <strong>{result.terminal_codigo}</strong>
          </p>
        )}
        {result?.valor && (
          <p className="text-slate-600">
            Valor:{" "}
            <strong>
              {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
                result.valor
              )}
            </strong>
          </p>
        )}
        <p className="text-sm text-slate-500 max-w-md mx-auto">
          O comprovante será processado automaticamente. Acompanhe na aba "Meus Comprovantes".
        </p>
        <Button onClick={resetForm}>Enviar outro comprovante</Button>
      </div>
    );
  }

  if (showCamera) {
    return <CameraGuide onCapture={handleCameraCapture} onClose={() => setShowCamera(false)} />;
  }

  const showOCRResult =
    imageBlob !== null && ocrStatus !== "idle" && ocrStatus !== "processing" && animationComplete;

  // Show the progressive receipt overlay once the OCR result arrives (not during "processing"
  // since result is null then) and until the animation finishes.
  const showProgressiveReceipt =
    imageBlob !== null && adaptedOcrResult !== null && !animationComplete;

  return (
    <div className="space-y-8 px-4 md:px-8">
      {/* Simple spinner overlay while waiting for OCR API response */}
      {ocrStatus === "processing" && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-white" />
        </div>
      )}

      {/* Progressive receipt animation — shown AFTER API returns, before animationComplete */}
      {showProgressiveReceipt && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <OCRProgressiveReceipt
              result={adaptedOcrResult}
              colorScheme="emerald"
              onComplete={() => setAnimationComplete(true)}
            />
          </div>
        </div>
      )}

      {/* ⋮ menu header */}
      <div className="relative mb-2">
        <div className="absolute top-0 right-0">
          <UploadFallbackMenu onPickFile={() => fileInputRef.current?.click()} />
        </div>
      </div>

      {/* Ponto Lotese */}
      <div className="space-y-2">
        <Label className="text-base font-medium">Ponto Lotese</Label>
        {terminaisLoading ? (
          <Skeleton className="h-11" />
        ) : terminais && terminais.length > 0 ? (
          <Select value={selectedTerminal} onValueChange={setSelectedTerminal}>
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Selecione o ponto Lotese" />
            </SelectTrigger>
            <SelectContent>
              {terminais.map((t) => (
                <SelectItem key={t.ges_ponto_id} value={t.ges_ponto_id}>
                  {t.codigo} — {t.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Alert>
            <AlertDescription>
              <strong>Nenhum ponto Lotese cadastrado para você.</strong>
              <br />
              Antes de enviar comprovantes, é preciso que o gestor da rede cadastre seus pontos
              Lotese (com o código GES correspondente). Entre em contato com o suporte da SE
              Promotora para solicitar.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Valor */}
      {selectedTerminal && (
        <div className="space-y-2">
          <Label className="text-base font-medium">Valor do depósito (R$)</Label>
          <Input
            type="text"
            inputMode="numeric"
            placeholder="0,00"
            value={valorDisplay}
            onChange={(e) => setValorDisplay(formatMoneyMask(e.target.value))}
            className="h-14 text-lg font-mono"
          />
        </div>
      )}

      {/* Capture: CTA grande + link discreto */}
      {selectedTerminal && valor > 0 && !imageBlob && !isCompressing && (
        <div className="space-y-3 pt-2">
          <Label className="text-base font-medium block">Foto do comprovante</Label>

          {!hasCameraSupport && (
            <div className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-md mb-2">
              Câmera não disponível. Use o menu ⋮ no canto superior direito pra enviar arquivo.
            </div>
          )}

          {hasCameraSupport && (
            <button
              type="button"
              onClick={() => setShowCamera(true)}
              className="w-full h-32 rounded-xl bg-gradient-to-br from-emerald-700 to-emerald-900 text-white shadow-lg flex flex-col items-center justify-center gap-2 transition-all duration-150 hover:shadow-xl hover:brightness-105 active:scale-[0.98]"
            >
              <Camera className="h-8 w-8" />
              <span className="text-base font-semibold uppercase tracking-wide">
                Tirar foto do comprovante
              </span>
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_COMPROVANTE}
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      )}

      {isCompressing && (
        <div className="text-center py-10 space-y-3">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
          <p className="text-sm text-slate-600">Processando imagem...</p>
        </div>
      )}

      {/* RN-3 — sem isto, a falha era silenciosa: o spinner acima parava e nada
          aparecia. A mensagem já vem da imageUtils dizendo a AÇÃO do parceiro. */}
      {erroImagem && !isCompressing && (
        <Alert variant="destructive" className="my-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{erroImagem}</AlertDescription>
        </Alert>
      )}

      {showOCRResult && imageBlob && (
        <OCRValidationResult
          status={ocrStatus}
          result={ocrResult}
          onConfirm={handleSubmit}
          onRetake={handleRetake}
          isSubmitting={uploadMutation.isPending}
        />
      )}

      {uploadMutation.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {(uploadMutation.error as { response?: { data?: { detail?: string } } })?.response?.data
              ?.detail || "Erro ao enviar comprovante"}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

const PortalComprovantesPage: React.FC = () => {
  const [tab, setTab] = useState("history");
  // Quando o parceiro clica "Enviar nova foto" no card, o envio precisa carregar
  // o vínculo com o comprovante pendenciado — senão a pendência não fecha e ele
  // acaba com dois registros abertos para o mesmo depósito.
  const [reenvio, setReenvio] = useState<{ id: string; ponto: string } | null>(null);

  // Pendências abertas: primeira página basta — o card é chamada à ação, não
  // relatório. Se houver muitas, a lista completa está no histórico abaixo.
  const { data } = usePortalComprovantes(1, null);
  const pendencias = ((data?.items ?? []) as PortalComprovante[])
    .filter((c) => c.pendencia?.aberta)
    .map((c) => ({
      id: c.id,
      ges_ponto_codigo: c.ges_ponto_codigo,
      ges_ponto_nome: c.ges_ponto_nome,
      valor: c.valor,
      pendencia: c.pendencia!,
    }));

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-emerald-900 mb-2">Comprovantes</h1>
        <p className="text-slate-600 text-sm">Envie e acompanhe seus comprovantes de depósito</p>
      </div>

      <div className="mb-6">
        <PendenciaCard
          pendencias={pendencias}
          onReenviar={(id, ponto) => {
            setReenvio({ id, ponto });
            setTab("upload");
          }}
        />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 bg-emerald-50/80 border border-emerald-100">
          <TabsTrigger
            value="history"
            className="data-[state=active]:bg-white data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm"
          >
            Meus Comprovantes
          </TabsTrigger>
          <TabsTrigger
            value="upload"
            className="data-[state=active]:bg-white data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm"
          >
            Enviar Comprovante
          </TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-6">
          <ComprovantesHistoryTab />
        </TabsContent>

        <TabsContent value="upload" className="mt-6 max-w-2xl mx-auto">
          <Card className="border-emerald-100/80 bg-white/80 backdrop-blur-sm">
            <CardHeader className="border-b border-emerald-50">
              <CardTitle className="flex items-center gap-2 text-emerald-900">
                <div className="h-9 w-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <Upload className="h-5 w-5" />
                </div>
                Enviar Novo Comprovante
              </CardTitle>
              <CardDescription>
                Selecione o ponto Lotese, informe o valor e tire uma foto do comprovante
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <ComprovantesUploadTab
                reenvio={reenvio}
                onReenvioConcluido={() => setReenvio(null)}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PortalComprovantesPage;
